import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { Paper, styled as muiStyled, useThemeProps } from '@mui/material';
import { a, config as springConfig, useSpring } from '@react-spring/web';
import { useBoolean, useDimension, useWindowSize } from '@repo/hooks';
import { useGesture } from '@use-gesture/react';
import styled from 'styled-components';

import { ToggleBarIcon } from 'Icons';

// Simple string concatenation function to replace clsx
const classNames = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

// Define types for the Collapse component
interface DraggableCollapseProps {
  defaultHeight?: number; // 기본 높이 (px)
  minHeight?: number; // 최소 높이 (px)
  maxHeight?: number; // 최대 높이 (px)
  draggable?: boolean;
  disabled?: boolean;
  open?: boolean;
  elevation?: number;
  className?: string;
  children?: React.ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  PaperProps?: Record<string, any>;
  sx?: any;
  style?: React.CSSProperties;
  toggleBarStyle?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
  dragCoefficient?: number;
  header?: React.ReactNode;
  forceHeight?: number;
  onChangeHeight?: (height: number) => void;
  onDragEnd?: (height: number) => void;
  bottomOffset?: number;
  showToggleBar?: boolean;
  collapseOffsetTop?: number;
  toggleBarIcon?: React.ReactNode;
}

interface OwnerState extends DraggableCollapseProps {
  open: boolean;
  elevation: number;
  classes?: Record<string, string>;
}

interface GestureState {
  direction: [number, number];
  movement: [number, number];
  velocity: [number, number];
  last: boolean;
  canceled?: boolean;
  dragging?: boolean;
  tap?: boolean;
  cancel?: () => void;
}

// Simple implementation to replace composeClasses
const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes = {} } = ownerState;
  return {
    root: classNames('root', classes.root),
    paper: classNames('paper', 'paperAnchorTop', classes.paper),
  };
};

const overridesResolver = (props: any, styles: any) => {
  return [styles.root];
};

const CollapseRoot = muiStyled('div', {
  name: 'MuiCollapse',
  slot: 'Root',
  overridesResolver,
})(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 10,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  visibility: 'hidden',
  pointerEvents: 'none',
  height: '100%',
  '&.MuiCollapse-open': {
    visibility: 'visible',
  },
}));

const CollapsePaper = muiStyled(Paper, {
  name: 'MuiCollapse',
  slot: 'Paper',
  overridesResolver: (props: any, styles: any) => {
    return [styles.paper, styles[`paperAnchorBottom`]];
  },
})(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 0 auto',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  borderRadius: '8px',
  boxShadow: 'none',
}));

const StyledAnimatedDiv = styled(a.div)`
  --collapse-width: 100%;
  --collapse-height: 100vh;
  --collapse-padding: 16px;
  --collapse-bottom-offset: 75px;

  padding-top: var(--collapse-offset-top);
  padding-left: var(--collapse-padding);
  padding-right: var(--collapse-padding);
  box-sizing: border-box;
  overflow-y: hidden;
  position: absolute;
  width: var(--collapse-width);
  height: var(--collapse-height);
  min-height: 10%;
  -webkit-overflow-scrolling: touch;
  touch-action: none;
  outline: none;
  display: flex;
  justify-content: center;
  pointer-events: auto;
  z-index: 10;
  left: 0;
  bottom: var(--collapse-bottom-offset);

  @media ${({ theme }) => theme.device.small} {
    --collapse-padding: 8px;
  }
`;

const DraggableCollapse = forwardRef<HTMLDivElement, DraggableCollapseProps>(function Collapse(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'MuiCollapse' }) as DraggableCollapseProps;

  const { previous: previousViewport, current: currentViewport, isReady: isViewportReady } = useWindowSize();
  const { height: viewportHeight } = currentViewport;

  const {
    defaultHeight = Math.floor(viewportHeight * 0.5),
    minHeight = Math.floor(viewportHeight * 0.1),
    maxHeight = Math.floor(viewportHeight * 0.9),
    draggable = true,
    disabled = false,
    open = false,
    elevation = 16,
    className,
    children,
    onOpen,
    onClose = () => {},
    PaperProps = {},
    style,
    dragCoefficient = 5,
    header,
    forceHeight,
    onChangeHeight,
    bottomOffset = 75,
    showToggleBar = true,
    onDragEnd,
    collapseOffsetTop: _collapseOffsetTop,
    toggleBarIcon = <ToggleBarIcon style={{ width: 120, height: 3 }} />,
    toggleBarStyle,
    bodyStyle,
    ...other
  } = props;

  const toggleBarHeight = showToggleBar ? 16 : 0;
  const collapseOffsetTop = header ? (_collapseOffsetTop ?? 0) : 0;

  const defaultPaperProps = {
    ...PaperProps,
    sx: {
      background: 'transparent',
      boxShadow: 'none',
      ...(PaperProps.sx || {}),
    },
  };

  const ownerState = {
    ...props,
    open,
    elevation,
    classes: {},
  };

  const currentHeight = useRef(0);

  /**
   * Boolean state for knowing if the container children is rendered
   */
  const { value: isRendered, setTrue: setRendered, setFalse: setNotRendered } = useBoolean(false);

  /**
   * Boolean state for control the toggle of collapse between draggable
   * and not draggable
   */
  const { value: toggleDraggable, toggle: handleDragToggle } = useBoolean(draggable);

  /**
   * String state to know which threshold collapse is currently at
   * @see {@link handleExpandToggle}
   */
  const [currentThreshold, setCurrentThreshold] = useState<'close' | 'default' | 'full'>('close');

  const classes = useUtilityClasses(ownerState);

  const [bodyRef, { dimension: bodyDimension }] = useDimension();
  const [paperRef, { dimension: paperDimension }] = useDimension();

  const getCollapseHeights = () => {
    return {
      closeHeight: minHeight + collapseOffsetTop,
      defaultHeight: defaultHeight + collapseOffsetTop,
      fullHeight: maxHeight + collapseOffsetTop,
    };
  };

  const { closeHeight, defaultHeight: currentDefaultHeight, fullHeight } = getCollapseHeights();

  const [{ height }, animate] = useSpring(
    () => ({
      height: forceHeight ?? currentDefaultHeight,
      onChange: ({ value: { height } }: { value: { height: number } }) => {
        onChangeHeight?.(height);
        if (height >= currentDefaultHeight - toggleBarHeight && height <= currentDefaultHeight + toggleBarHeight) {
          setCurrentThreshold('default');
          return;
        }

        if (height >= fullHeight - toggleBarHeight && height <= fullHeight + toggleBarHeight) {
          setCurrentThreshold('full');
          return;
        }
      },
    }),
    [currentDefaultHeight, fullHeight, forceHeight]
  );

  // disabled 상태가 변경될 때 현재 진행 중인 drag를 취소하고 초기 상태로 되돌림
  useEffect(() => {
    if (disabled) {
      // 현재 진행 중인 drag를 취소하고 초기 상태로 되돌림
      const currentHeightValue = height.get();
      animate.start({
        height: forceHeight ?? currentHeightValue,
        config: {
          tension: 170,
          friction: 26,
        },
      });
    }
  }, [disabled, forceHeight]);

  /**
   * Get different configuration of spring animation based on threshold
   */
  const configOf = (state: { canceled?: boolean; velocity?: number }, type: 'once' | 'close' | 'default' | 'full') => {
    switch (type) {
      case 'once':
        return {
          tension: 246,
          friction: 32,
        };
      case 'close':
        return { ...springConfig.stiff, ...state };
      case 'default':
        return state.canceled ? springConfig.wobbly : springConfig.stiff;
      case 'full':
        return state.canceled ? springConfig.wobbly : springConfig.stiff;
      default:
        throw Error('invalid animate type!');
    }
  };

  /**
   * Animate collapse to default height
   */
  const toDefaultHeight = (param?: { canceled?: boolean }) => {
    const defaultParam = param || { canceled: false };
    animate.start({
      immediate: false,
      height: currentDefaultHeight,
      config: configOf(defaultParam, 'default'),
    });
  };

  /**
   * Animate collapse to close height
   */
  const toCloseHeight = (param?: { velocity?: number }) => {
    const defaultParam = param || { velocity: 0 };
    animate.start({
      immediate: false,
      height: closeHeight,
      config: configOf(defaultParam, 'close'),
      onResolve: () => {
        currentHeight.current = 0;
        onClose();
      },
    });
  };

  /**
   * Animate collapse to full height
   */
  const toFullHeight = (param?: { canceled?: boolean }) => {
    const defaultParam = param || {
      canceled: false,
    };
    animate.start({
      immediate: false,
      height: fullHeight,
      config: configOf(defaultParam, 'full'),
    });
  };

  const resetToClosestThreshold = (param?: { canceled?: boolean }) => {
    const { canceled } = param || {
      canceled: false,
    };
    if (currentThreshold === 'default') {
      toDefaultHeight({ canceled });
      return;
    }
    if (currentThreshold === 'full') {
      toFullHeight({ canceled });
      return;
    }
  };

  const handleDragCancelGesture = (gestureState: GestureState) => {
    const {
      direction: [, dy],
      movement: [, my],
      cancel,
    } = gestureState;
    const direction = dy > 0 ? 'down' : 'up';
    const belowDefaultThreshold = my > 40;
    const belowFullThreshold = my > 40;
    if (direction === 'down' && currentThreshold === 'default' && belowDefaultThreshold) {
      cancel?.();
    }
    if (direction === 'down' && currentThreshold === 'full' && belowFullThreshold) {
      cancel?.();
    }
  };

  /**
   * Check if collapse should close or not.
   *
   * To close the collapse, return true.
   *
   * To cancel the action, return false.
   */
  const shouldCollapseClose = (gestureState: GestureState) => {
    const {
      direction: [, dy],
      velocity: [, vy],
      movement: [, my],
    } = gestureState;
    const direction = dy > 0 ? 'down' : 'up';
    const aboveCloseThreshold = my < -60;
    const isQuickDrag = vy <= -2.0;
    const aboveCloseHeight = height.get() < viewportHeight * 0.2 - 100;
    if (aboveCloseHeight) {
      return true;
    }
    if (direction === 'up' && isQuickDrag && aboveCloseThreshold) {
      return true;
    }
    return false;
  };

  /**
   * Handler to decide which drag direction is current invoking
   */
  const handleDragDirection = (gestureState: GestureState) => {
    const {
      direction: [, dy],
    } = gestureState;
    const direction = dy > 0 ? 'down' : 'up';
    direction === 'down' ? handleDragDirectionDown(gestureState) : handleDragDirectionUp(gestureState);
  };

  /**
   * Handle drag direction up
   */
  const handleDragDirectionUp = (gestureState: GestureState) => {
    const {
      movement: [, my],
      velocity: [, vy],
      canceled,
      last,
    } = gestureState;
    if (last) {
      const isFlickUpward = vy < -0.2;
      const isAboveDefaultThreshold = my < -40;
      if (isFlickUpward || isAboveDefaultThreshold) {
        toFullHeight({ canceled });
      } else {
        resetToClosestThreshold({ canceled });
      }
    }
  };

  /**
   * Handle drag direction down
   */
  const handleDragDirectionDown = (gestureState: GestureState) => {
    const close = shouldCollapseClose(gestureState);
    const {
      movement: [, my],
      velocity: [, vy],
      canceled,
      last,
    } = gestureState;
    if (last) {
      const isBelowFullHeightThreshold = my > 60;
      if (close) {
        toCloseHeight({ velocity: vy });
      } else if (isBelowFullHeightThreshold) {
        toDefaultHeight({ canceled });
      } else {
        resetToClosestThreshold({ canceled });
      }
    }
  };

  /**
   * When the user keeps dragging, we just change the height according to
   * the cursor position
   */
  const handleCursorPositionChange = (gestureState: GestureState) => {
    const {
      last,
      movement: [, my],
    } = gestureState;
    if (!last) {
      animate.start({
        immediate: true,
        height: currentHeight.current - my,
      });
    }
  };

  /**
   * When the user keep dragging, we don't want to user to drag the
   * collapse over the full height threshold which could lead to
   * NaN value and animation crash.
   */
  const handleCollapseDrag = (gestureState: GestureState) => {
    const {
      movement: [, my],
    } = gestureState;

    const newHeight = currentHeight.current - my * dragCoefficient;

    // 드래그 방향에 따라 높이 제한 설정
    const minHeight = Math.max(0, closeHeight);
    const maxHeight = fullHeight;

    // 높이 범위 내에서만 애니메이션 적용
    if (newHeight >= minHeight && newHeight <= maxHeight) {
      animate.start({
        immediate: false,
        height: newHeight,
      });
    }
  };

  /**
   * When the user drag the collapse to close to the close threshold,
   * if above the close threshold then we close the collapse.
   * @see {@link shouldCollapseClose}
   */
  const handleNearCloseThreshold = (gestureState: GestureState) => {
    const close = shouldCollapseClose(gestureState);
    const {
      velocity: [, vy],
    } = gestureState;
    if (close) {
      toCloseHeight({ velocity: vy });
    } else {
      // 드래그 종료 시 현재 높이 유지
      const currentHeightValue = height.get();
      animate.start({
        immediate: true,
        height: currentHeightValue,
      });
    }
  };

  /**
   * Hook for handle creation of gestures handler
   */
  const bind = useGesture(
    {
      onDragStart: (gestureState: GestureState) => {
        if (disabled) {
          gestureState.cancel?.();
          return;
        }
        const { dragging } = gestureState;
        if (dragging) {
          currentHeight.current = height.get();
        }
      },
      onDrag: (gestureState: GestureState) => {
        if (disabled) {
          gestureState.cancel?.();
          return;
        }
        const { tap } = gestureState;
        if (tap) return;
        if (!toggleDraggable) {
          handleDragCancelGesture(gestureState);
          handleDragDirection(gestureState);
          handleCursorPositionChange(gestureState);
        } else {
          handleCollapseDrag(gestureState);
        }
      },
      onDragEnd: (gestureState: GestureState) => {
        onDragEnd?.(height.get());
        if (disabled) {
          gestureState.cancel?.();
          return;
        }
        handleNearCloseThreshold(gestureState);
      },
    },
    {
      drag: {
        from: () => [0, 0],
        delay: 0,
        filterTaps: true,
        bounds: {
          top: 0,
          bottom: 0,
        },
        rubberband: true,
        enabled: !disabled,
      },
    }
  );

  /**
   * Callback that handle toggle between default height to full height
   * then back and forth
   */
  const handleExpandToggle = () => {
    currentThreshold === 'full' ? toDefaultHeight({}) : toFullHeight({});
  };

  /**
   * Side effect that wait for container to be rendered
   */
  useEffect(() => {
    if (open) {
      const timeout = setTimeout(() => {
        setRendered();
        onOpen?.();
      }, 0);
      return () => {
        setNotRendered();
        clearTimeout(timeout);
      };
    }
  }, [open, onOpen]);

  /**
   * Side effect that open collapse but wait until the container
   * children is rendered
   */
  useEffect(() => {
    if (open && isRendered) {
      toDefaultHeight({});
    } else if (!open) {
      toCloseHeight({});
    }
    return () => {
      if (!open) {
        onClose();
      }
    };
  }, [isRendered, bodyDimension, open]);

  /**
   * Side effect that keep the collapse height
   * the same across different window height
   */
  useEffect(() => {
    const getHeight = () => {
      const previousHeight = height.get();
      const previousViewportHeight = previousViewport?.height ?? currentViewport.height;
      const currentViewportHeight = currentViewport.height;
      const updateHeight = (previousHeight * currentViewportHeight) / previousViewportHeight;
      if (updateHeight >= currentViewportHeight) {
        return Math.min(updateHeight, currentViewportHeight);
      }
      return updateHeight;
    };

    if (isViewportReady && previousViewport) {
      currentHeight.current = getHeight();
    }
  }, [currentViewport]);

  return (
    <CollapseRoot
      className={classNames(classes.root, open ? 'MuiCollapse-open' : '', className)}
      ref={ref}
      style={style}
      {...other}
    >
      <StyledAnimatedDiv
        className="MuiCollapsePaper-Wrapper"
        style={
          {
            ['--collapse-offset-top' as string]: `${collapseOffsetTop}px`,
            ['--collapse-bottom-offset' as string]: `${bottomOffset}px`,
            height,
            display: open ? 'flex' : 'none',
            overflow: 'hidden',
          } as any
        }
      >
        <CollapsePaper
          elevation={elevation}
          square
          {...defaultPaperProps}
          className={classNames(classes.paper, PaperProps?.className)}
          ref={paperRef}
        >
          {header &&
            React.cloneElement(header as React.ReactElement, {
              style: {
                ...(header as React.ReactElement).props.style,
                width: paperDimension?.width || '100%',
              },
            })}
          <div
            {...bind()}
            style={{
              touchAction: 'none',
              display: showToggleBar ? 'flex' : 'none',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px 14px',
              height: toggleBarHeight,
              cursor: 'grab',
              zIndex: 10,
              ...toggleBarStyle,
            }}
          >
            {toggleBarIcon}
          </div>
          <div
            ref={bodyRef}
            style={{
              overflow: 'hidden',
              maxHeight: showToggleBar ? `calc(100% - ${toggleBarHeight}px)` : '100%',
              flex: 1,
              borderRadius: '0 0 8px 8px',
              padding: `12px 0px 8px 16px`,
              ...bodyStyle,
            }}
          >
            <div style={{ height: '100%', overflow: 'auto', paddingRight: '16px' }}>{children}</div>
          </div>
        </CollapsePaper>
      </StyledAnimatedDiv>
    </CollapseRoot>
  );
});

export { DraggableCollapse };
export type { DraggableCollapseProps };
